import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Building2, 
  LogOut, 
  Plus, 
  TrendingUp,
  Users,
  Star,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";

const API_URL = '';
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_qr-navigator/artifacts/rwd34j12_logo%20%284%29.png";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user if not in state
        if (!user) {
          const userResponse = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include'
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUser(userData);
          }
        }

        // Fetch stats
        const statsResponse = await fetch(`${API_URL}/api/admin/stats`, {
          credentials: 'include'
        });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Logged out successfully');
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 hidden md:block">
        <div className="p-6 flex items-center gap-3">
          <img 
            src={LOGO_ICON} 
            alt="Disqover" 
            className="h-8 w-8 object-contain"
          />
          <div>
            <p className="text-base font-semibold text-slate-900 font-heading">Disqover</p>
            <p className="text-xs text-slate-500 font-body">Admin Portal</p>
          </div>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link
            to="/admin"
            className="sidebar-link-active flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link
            to="/admin/businesses"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <Building2 className="w-5 h-5" />
            Businesses
          </Link>
          <Link
            to="/admin/analytics"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <BarChart3 className="w-5 h-5" />
            Creator Analytics
          </Link>
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-teal-600 font-medium">{user?.name?.[0] || 'A'}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="w-full"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-bold text-slate-900 font-heading">Admin</h1>
        <Button onClick={handleLogout} variant="ghost" size="sm">
          <LogOut className="w-5 h-5" />
        </Button>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-heading">Dashboard</h2>
              <p className="text-slate-500 font-body">Overview of your business listings</p>
            </div>
            <Link to="/admin/businesses/new">
              <Button className="rounded-full bg-teal-600 hover:bg-teal-700" data-testid="add-business-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Business
              </Button>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-teal-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-heading">
                {stats?.total_businesses || 0}
              </p>
              <p className="text-sm text-slate-500 font-body">Total Businesses</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-heading">
                {stats?.active_businesses || 0}
              </p>
              <p className="text-sm text-slate-500 font-body">Active Listings</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-heading">
                {stats?.featured_count || 0}
              </p>
              <p className="text-sm text-slate-500 font-body">Featured Boost</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-heading">
                {stats?.total_businesses - (stats?.featured_count || 0)}
              </p>
              <p className="text-sm text-slate-500 font-body">Core Listing</p>
            </div>
          </div>

          {/* Category Breakdown */}
          {stats?.category_counts && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 font-heading mb-4">
                Businesses by Category
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(stats.category_counts).map(([category, count]) => (
                  <div key={category} className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-xl font-bold text-slate-900 font-heading">{count}</p>
                    <p className="text-sm text-slate-500 font-body">{category}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-around">
        <Link to="/admin" className="flex flex-col items-center text-teal-600">
          <LayoutDashboard className="w-6 h-6" />
          <span className="text-xs mt-1">Dashboard</span>
        </Link>
        <Link to="/admin/businesses" className="flex flex-col items-center text-slate-400">
          <Building2 className="w-6 h-6" />
          <span className="text-xs mt-1">Businesses</span>
        </Link>
        <Link to="/admin/analytics" className="flex flex-col items-center text-slate-400">
          <BarChart3 className="w-6 h-6" />
          <span className="text-xs mt-1">Analytics</span>
        </Link>
      </nav>
    </div>
  );
}
